import { React, useState, useEffect, useRef, useCallback } from 'react';
import { Navigation, Pagination, Autoplay, Zoom, Mousewheel} from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useData } from '../../contexts/DataContext';
import { Link as RouterLink } from 'react-router-dom';
import { Link as ScrollLink } from 'react-scroll'
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { BsFacebook, BsInstagram } from 'react-icons/bs';
import { FaChevronDown } from 'react-icons/fa';
import { MdFullscreen } from "react-icons/md";

const VideoModal = ({ isOpen, onClose, videoSrc, currentTime }) => {
  const modalVideoRef = useRef(null);
  const backdropRef = useRef(null);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  const handleBackdropClick = (event) => {
    if (event.target === backdropRef.current) {
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      // Add event listeners when modal opens
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    return () => {
      // Cleanup when modal closes
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'visible';
    };
  }, [isOpen, handleKeyDown]);

  useEffect(() => {
    if (isOpen && modalVideoRef.current && currentTime) {
      modalVideoRef.current.currentTime = currentTime;
      modalVideoRef.current.play().catch(console.error);
    }
  }, [isOpen, currentTime]);

  const handleClose = () => {
    if (modalVideoRef.current) {
      modalVideoRef.current.pause();
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      ref={backdropRef}
      onClick={handleBackdropClick}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-90 cursor-pointer"
    >
      <div className="relative mx-4 w-full max-w-4xl cursor-auto" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={handleClose}
          className="absolute right-0 -top-10 z-50 p-2 text-white bg-red-600 rounded-full transition-colors hover:bg-red-700"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <div className="relative pt-[56.25%]">
          <video
            ref={modalVideoRef}
            className="object-cover absolute top-0 left-0 w-full h-full rounded-lg"
            controls
            autoPlay
            muted
          >
            <source src={videoSrc} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
    </div>
  );
};

const Hero = () => {
  const { t } = useTranslation();
  const result = useData();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const location = useLocation();
  const navigate = useNavigate();
  const isLoading = !result.data;
  const hero_slides = result.data?.hero || [];
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [currentVideoTime, setCurrentVideoTime] = useState(0);
  const [showScrollButton, setShowScrollButton] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const slides = isLoading ? [] : hero_slides.slides.map(slide => ({
    ...slide,
    image: '/assets/img_01.webp',
  }));

  const SlideBackground = ({ slide, index, isModalOpen }) => {
    const videoRef = useRef(null);
    const [isVideoLoaded, setIsVideoLoaded] = useState(false);
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const lastPlaybackPosition = useRef(0);
    const playPromiseRef = useRef(null);

    useEffect(() => {
      let observer;
      if (videoRef.current && slide.video && !isModalOpen) {
        const playVideo = () => {
          if (!isModalOpen && videoRef.current) {
            playPromiseRef.current = videoRef.current.play();
            playPromiseRef.current
              .then(() => setIsVideoPlaying(true))
              .catch(console.error);
          }
        };

        const pauseVideo = () => {
          if (videoRef.current && playPromiseRef.current) {
            playPromiseRef.current.then(() => {
              videoRef.current.pause();
              setIsVideoPlaying(false);
            }).catch(() => { });
          }
        };

        observer = new IntersectionObserver(
          (entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting && !isModalOpen) {
                videoRef.current.currentTime = lastPlaybackPosition.current;
                playVideo();
              } else {
                pauseVideo();
              }
            });
          },
          { threshold: 0.1 }
        );

        observer.observe(videoRef.current);
      }

      return () => {
        if (observer) observer.disconnect();
        if (playPromiseRef.current) {
          playPromiseRef.current.then(() => {
            if (videoRef.current) {
              lastPlaybackPosition.current = videoRef.current.currentTime;
              videoRef.current.pause();
            }
          }).catch(() => { });
        }
      };
    }, [slide.video, isModalOpen]);

    const handleVideoTimeUpdate = () => {
      if (videoRef.current) {
        lastPlaybackPosition.current = videoRef.current.currentTime;
      }
    };

    const handleFullscreen = () => {
      if (videoRef.current) {
        const currentTime = videoRef.current.currentTime;
        videoRef.current.pause();
        setIsVideoPlaying(false);

        setTimeout(() => {
          setCurrentVideoTime(currentTime);
          setSelectedVideo(slide.video);
          setIsModalOpen(true);
        }, 50);
      }
    };

    return (
      <div className="absolute inset-0">
        <img
          src={slide.image}
          alt={slide.title}
          className={`object-cover w-full h-full transition-opacity duration-700 ${slide.video && isVideoLoaded && isVideoPlaying ? 'opacity-0' : 'opacity-100'
            }`}
        />

        {slide.video && (
          <>
            <video
              ref={videoRef}
              className={`absolute inset-0 object-cover w-full h-full transition-opacity duration-700 ${isVideoLoaded && isVideoPlaying ? 'opacity-100' : 'opacity-0'
                }`}
              playsInline
              muted
              loop
              preload="auto"
              onTimeUpdate={handleVideoTimeUpdate}
              onLoadedData={() => setIsVideoLoaded(true)}
            >
              <source src={slide.video} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <button
              onClick={handleFullscreen}
              className={`absolute bottom-4 z-10 px-4 py-2 text-white bg-black/20 rounded-full hover:bg-black/40 transition-all duration-300 transform hover:scale-105 ${isRTL ? 'left-4' : 'right-4'
                }`}
              aria-label={t('fullscreen')}
            >
              <MdFullscreen className="w-6 h-6" />
            </button>
          </>
        )}
      </div>
    );
  };

  // ... (keep remaining Hero component code identical from original, except for the SwiperSlide mapping below)

  return (
    <section id="hero" className="relative hero-section">
      {isLoading ? (
        <div className="flex justify-center items-center h-screen">
          <div className="animate-pulse">
            <div className="mb-4 h-96 bg-gray-300 rounded-lg"></div>
            <div className="mb-2 h-6 bg-gray-300 rounded"></div>
            <div className="mb-2 h-4 bg-gray-300 rounded"></div>
            <div className="h-4 bg-gray-300 rounded"></div>
          </div>
        </div>
      ) : (
        <Swiper
          modules={[Navigation, Pagination, Autoplay, Zoom, Mousewheel]}
          dir={isRTL ? 'rtl' : 'ltr'}
          spaceBetween={0}
          slidesPerView={1}
          className="h-screen hero-swiper"
        >
          {slides.map((slide, index) => (
            <SwiperSlide key={slide.id}>
              <div className="relative h-full">
                <SlideBackground
                  slide={slide}
                  index={index}
                  isModalOpen={isModalOpen}
                />
                <div className="absolute inset-0 bg-black opacity-60" />
                <div className="absolute bottom-20 left-0 right-0 z-10">
                  <div className="container px-4 mx-auto">
                    <div className={`w-full lg:w-1/2 ${isRTL ? 'font-arabic' : ''}`}>
                      <h1
                        className="mb-6 text-4xl font-bold leading-tight text-white lg:text-5xl animate-fade-in"
                        dangerouslySetInnerHTML={{ __html: slide.title }}
                      />
                      <div className="flex flex-wrap gap-4 animate-fade-in-delay">
                        {slide.btn?.map((item, index) => (
                          <button
                            key={item.id || `btn-${index}`}
                            className="flex gap-2 items-center transition-colors cursor-pointer btn-primary"
                            onClick={() => window.location.href = item.href}
                          >
                            {item.title}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      )}

      <VideoModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setCurrentVideoTime(0);
        }}
        videoSrc={selectedVideo}
        currentTime={currentVideoTime}
      />

      <div className={`absolute bottom-4 z-10 left-1/2 -translate-x-1/2 transition-opacity duration-300 ${showScrollButton ? 'opacity-100' : 'opacity-0'
        }`}>
        <button
          onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
          className="px-4 py-2 text-white bg-black/20 rounded-full hover:bg-black/40 transition-all duration-300 animate-pulse"
          aria-label="Scroll to About section"
        >
          <FaChevronDown className="w-6 h-6" />
        </button>
      </div>
    </section>
  );
};

export default Hero;